import { IntakeFormData } from '../../types/intake'

const CODE = {
  gender: { male: 1, female: 2 } as const,

  inPerson: { yes: 1, maybe: 2, not_now: 3 } as const,

  languages: { en: 1, fr: 2, ar: 3, ur: 4 } as const,

  agePrefs: {
    '18–24': 1,
    '25–30': 2,
    '31–35': 3,
    '36–41': 4,
    '42–46': 5,
    '47+': 6,
  } as const,

  childrenCount: { '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6+': 7 } as const,
};

function mapArray<T extends Record<string, number>>(arr: string[], dict: T): number[] {
  return arr
    .map((x) => dict[x as keyof T])
    .filter((v): v is number => typeof v === 'number')
}

export function buildIntakePayload(formData: IntakeFormData) {
  if (!formData.email || formData.email.length < 3) {
    throw new Error('Email missing')
  }

  const intakeRole = (formData as any).intakeRole || 'candidate'

  const ageNum = formData.age ? parseInt(formData.age, 10) : null
  const rcAgeNum = (formData as any).rc_age ? parseInt((formData as any).rc_age, 10) : null

  // ✅ Candidate marital/children fields (always about the CANDIDATE, even when guardian is filling it out)
  const previouslyMarried =
    typeof formData.previouslyMarried === 'boolean' ? formData.previouslyMarried : null

  const hasChildren =
    previouslyMarried === true && typeof formData.hasChildren === 'boolean'
      ? formData.hasChildren
      : null

  const childrenLivingWith =
    previouslyMarried === true && hasChildren === true
      ? (formData.childrenLivingWith?.trim() || null)
      : null

  const openToMoreChildren =
    previouslyMarried === true ? (formData.openToMoreChildren?.trim() || null) : null

  return {
    locale: formData.locale,
    intake_version: formData.intake_version,

    email: formData.email.trim().toLowerCase(),
    phone: formData.phone?.trim() || null,

    data: {
      // -------------------------
      // EXISTING (candidate) DATA
      // -------------------------
      first_name: formData.firstName?.trim() || null,
      age: ageNum,
      gender: formData.gender ? CODE.gender[formData.gender] : null,
      city: formData.city?.trim() || null,
      in_person_events: formData.inPerson
        ? CODE.inPerson[formData.inPerson as keyof typeof CODE.inPerson]
        : null,

      // Photo (storage reference only)
      photo_path: (formData as any).photo_path ?? null,

      languages_spoken: mapArray(formData.languagesSpoken || [], CODE.languages),
      other_language_text: formData.otherLanguage?.trim() || null,

      languages_preferred: mapArray(formData.languagesPreferred || [], CODE.languages),
      other_language_preferred_text: formData.otherLanguagePreferred?.trim() || null,

      // scales (assumed already numbers)
      lifestyle_activity: formData.lifestyle_activity,
      lifestyle_travel: formData.lifestyle_travel,
      lifestyle_vacation: formData.lifestyle_vacation,
      lifestyle_exercise: formData.lifestyle_exercise,
      lifestyle_diet: formData.lifestyle_diet,

      rel_romance: formData.rel_romance,
      rel_conflict: formData.rel_conflict,
      rel_processing: formData.rel_processing,
      rel_similarity: formData.rel_similarity,
      rel_openness: formData.rel_openness,

      fam_marriage_age: formData.fam_marriage_age,

      // keep your existing "desired children" count
      fam_children_count: formData.fam_children_count
        ? CODE.childrenCount[formData.fam_children_count as keyof typeof CODE.childrenCount]
        : null,

      fam_finances: formData.fam_finances,
      fam_inlaws: formData.fam_inlaws,
      fam_parents_decisions: formData.fam_parents_decisions,

      // ✅ Candidate family fields (candidate accounts write these; guardian accounts should use rc_* below)
      previously_married: intakeRole !== 'guardian' ? previouslyMarried : null,
      has_children: intakeRole !== 'guardian' ? hasChildren : null,
      children_living_with: intakeRole !== 'guardian' ? childrenLivingWith : null,
      open_to_more_children: intakeRole !== 'guardian' ? openToMoreChildren : null,

      faith_role: formData.faith_role,
      faith_halal_food: formData.faith_halal_food,
      faith_ramadan: formData.faith_ramadan,
      faith_halal_income: formData.faith_halal_income,

      faith_denomination_importance: formData.faith_denomination_importance ?? null,

      faith_denomination:
        (formData.faith_denomination_importance ?? 0) >= 6
          ? (formData.faith_denomination || null)
          : null,

      faith_denomination_other:
        (formData.faith_denomination_importance ?? 0) >= 6 &&
        formData.faith_denomination === 'other'
          ? (formData.faith_denomination_other?.trim() || null)
          : null,

      // conditional fields: keep null if not shown/answered
      faith_children: formData.faith_children ?? null,
      open_children_abroad: formData.open_children_abroad ?? null,
      open_children_lang: formData.open_children_lang ?? null,

      open_long_distance: formData.open_long_distance,
      open_financial_risk: formData.open_financial_risk,
      open_financial_support: formData.open_financial_support,

      age_pref_ranges: mapArray(formData.age_preferences || [], CODE.agePrefs),

      reflection: formData.reflection?.trim() || null,

      // -------------------------
      // NEW: ROLE + CONTROLS
      // -------------------------
      intake_role: intakeRole,

      // Candidate controls (only meaningful for candidate accounts)
      allow_guardian_led_intros:
        intakeRole === 'candidate' && typeof (formData as any).allowGuardianLedIntros === 'boolean'
          ? (formData as any).allowGuardianLedIntros
          : null,

      guardian_intro_roles:
        intakeRole === 'candidate' && Array.isArray((formData as any).guardianIntroRoles)
          ? (formData as any).guardianIntroRoles
          : null,

      // -------------------------
      // NEW: GUARDIAN FLOW EXTRAS
      // -------------------------
      ...(intakeRole === 'guardian'
        ? {
            guardian_relationship: (formData as any).guardianRelationship || null,
            candidate_approves_every_intro: true,

            // represented candidate basics
            rc_first_name: (formData as any).rc_firstName?.trim() || null,
            rc_age: rcAgeNum,
            rc_gender: (formData as any).rc_gender
              ? CODE.gender[(formData as any).rc_gender]
              : null,
            rc_city: (formData as any).rc_city?.trim() || null,

            rc_languages_spoken: mapArray((formData as any).rc_languagesSpoken || [], CODE.languages),
            rc_other_language_text: (formData as any).rc_otherLanguage?.trim() || null,
            rc_languages_preferred: mapArray(
              (formData as any).rc_languagesPreferred || [],
              CODE.languages,
            ),
            rc_other_language_preferred_text:
              (formData as any).rc_otherLanguagePreferred?.trim() || null,

            // ✅ MUST be about the represented candidate
            rc_previously_married: previouslyMarried,
            rc_has_children: hasChildren,
            rc_children_living_with: childrenLivingWith,
            rc_open_to_more_children: openToMoreChildren,

            // spouse preferences (guardian-only)
            pref_faith_role: (formData as any).pref_faith_role ?? null,
            pref_faith_ramadan: (formData as any).pref_faith_ramadan ?? null,
            pref_faith_halal_food: (formData as any).pref_faith_halal_food ?? null,
            pref_faith_halal_income: (formData as any).pref_faith_halal_income ?? null,

            pref_fam_inlaws: (formData as any).pref_fam_inlaws ?? null,
            pref_fam_parents_decisions: (formData as any).pref_fam_parents_decisions ?? null,
            pref_fam_finances: (formData as any).pref_fam_finances ?? null,

            pref_rel_conflict: (formData as any).pref_rel_conflict ?? null,
            pref_rel_openness: (formData as any).pref_rel_openness ?? null,
            pref_rel_processing: (formData as any).pref_rel_processing ?? null,

            pref_lifestyle_travel: (formData as any).pref_lifestyle_travel ?? null,
            pref_lifestyle_exercise: (formData as any).pref_lifestyle_exercise ?? null,
          }
        : {}),
    },
  }
}